import React, { useEffect, useState } from 'react';
import { API_URLS } from '../apiUrl';
import PropertyTypeDropdown from '../pages/customHooks/propertyTypeDropdown';
import PropertyTypeDescriptionDropdown from '../pages/customHooks/propertyTypeDescriptionDropdown';
import UseOfPropertyDropdown from '../pages/customHooks/useOfPropertyDropdown';
import PurposeOfPropertyDropdown from '../pages/customHooks/purposeOfPropertyDropdown';
import axiosInstance from '../components/axiosInstance';

const PropertyDescriptionEdit = ({ isOpen, closeModal, propertyDescription, propertyDescriptionId, propertyTypeId, propertyTypeDescriptionId, useOfPropertyId, purposeOfUseId, propertyTypeName, propertyTypeDescriptionName, useOfPropertyName, purposeOfPropertyName }: any) => {

    const [formData, setFormData] = useState<any>({
        property_type_id: "",
        property_type_description_id: "",
        use_of_property_id: "",
        purpose_of_use_id: "",
        description_name: "",
        property_description_id: propertyDescriptionId
    });

    useEffect(() => {
        if (isOpen) {
            setFormData((prevState: any) => ({
                ...prevState,
                property_description_id: propertyDescriptionId
            }));
        }
    }, [isOpen, propertyDescriptionId]);

    const [errors, setErrors] = useState<any>({});
    const [successMessage, setSuccessMessage] = useState<string>("");
    const [dataExistMessage, setDataExistMessage] = useState<string>("");

    const [inputValue, setInputValue] = useState('');
    const [tags, setTags] = useState<{ id: string; name: string }[]>([]);

    // Initialize tags from props
    useEffect(() => {
        let parsedTags: { id: string; name: string }[] = [];

        if (typeof propertyDescription === 'string') {
            parsedTags = propertyDescription.split(',').map((tag, index) => ({
                id: `tag-${index + 1}`,
                name: tag.trim(),
            }));
        } else if (Array.isArray(propertyDescription)) {
            parsedTags = propertyDescription.map((tag: { id: string; name: string }) => ({
                id: tag.id,
                name: tag.name,
            }));
        }

        setTags(parsedTags);
    }, [propertyDescription]);

    // Remove tag function
    const removeTag = (tagId: string) => {
        const newTags = tags.filter(t => t.id !== tagId);
        setTags(newTags);
        setFormData((prevData: any) => ({
            ...prevData,
            description_name: newTags.map(t => t.name).join(", "),
        }));
    };

    // Add tag function
    const addTag = (tag: string) => {
        if (tag && !tags.some(t => t.name === tag)) {
            const newTags = [
                ...tags,
                {
                    id: `tag-${tags.length + 1}`,
                    name: tag.trim(),
                }
            ];
            setTags(newTags);
            setFormData((prevData: any) => ({
                ...prevData,
                description_name: newTags.map(t => t.name).join(", "),
            }));
            setInputValue(''); // Clear input after adding tag
        }
    };

    const handleKeyPress = (e: any) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (inputValue.trim()) {
                addTag(inputValue.trim());
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validation
        if (tags.length === 0) {
            setErrors({ description_name: "Description name is required" });
            return;
        }

        // Use prop values if dropdowns are not selected
        const updatedFormData = {
            property_type_id: formData.property_type_id || propertyTypeId,
            property_type_description_id: formData.property_type_description_id || propertyTypeDescriptionId,
            use_of_property_id: formData.use_of_property_id || useOfPropertyId,
            purpose_of_use_id: formData.purpose_of_use_id || purposeOfUseId,
            property_description_id: propertyDescriptionId,
            description_name: tags.map(tag => tag.name).join(", ")
        };

        try {
            const response = await axiosInstance.put(`${API_URLS.PropertyDescriptionUpdate}`, updatedFormData);

            if (response.data.message) {
                setSuccessMessage(response.data.message);
                setTimeout(() => {
                    setSuccessMessage("");
                    closeModal();
                }, 5000);
            }
            setErrors({});
        } catch (error) {
            setDataExistMessage("Property Description already exists. Please choose a different name.");
            setTimeout(() => {
                setDataExistMessage("");
                closeModal();
            }, 5000);
        }
    };

    // Hooks for dropdowns
    const [handleDropdownToggle, isDropdownOpen, propertyTypes, loading, setIsDropdownOpen] = PropertyTypeDropdown();
    const [, isDropdownOpenPropertyDescription, , propertyDescriptions, loadingPropertyDescription, setIsDropdownOpenPropertyDescription] = PropertyTypeDescriptionDropdown(formData.property_type_id);
    const [setIsDropdownOpenUseOfProperty, isDropdownOpenUseOfProperty, , useOfProperty, loadingUseOfProperty] = UseOfPropertyDropdown(formData.property_type_id, formData.property_type_description_id);
    const [setIsDropdownOpenPurposeOfProperty, isDropdownOpenPurposeOfProperty, , purposeOfProperty, loadingPurposeOfProperty] = PurposeOfPropertyDropdown(formData.property_type_id, formData.property_type_description_id, formData.use_of_property_id);

    const handlePropertyTypeSelect = (selectedValue: any) => {
        setFormData((prevState: any) => ({
            ...prevState,
            property_type_id: selectedValue,
            // Reset dependent dropdowns
            property_type_description_id: "",
            use_of_property_id: "",
            purpose_of_use_id: "",
        }));
        setIsDropdownOpen(false);
    };

    const selectedPropertyTypeName = propertyTypes.find((property: any) => property.type_primary_id === formData.property_type_id)?.type_name || propertyTypeName;

    const handlePropertyTypeDescriptionSelect = (e: any) => {
        const { value, checked } = e.target;

        setFormData((prevFormData: any) => {
            let newSelections = prevFormData.property_type_description_id ? prevFormData.property_type_description_id.split(",") : [];

            if (checked) {
                newSelections.push(value);
            } else {
                const index = newSelections.indexOf(value);
                if (index > -1) {
                    newSelections.splice(index, 1);
                }
            }

            return {
                ...prevFormData,
                property_type_description_id: newSelections.join(","),
            };
        });
    };

    const selectedPropertyDescriptions = propertyDescriptions
        .filter((property: any) => formData.property_type_description_id?.split(",").includes(String(property.id)))
        .map((property: any) => property.type_description_name)
        .join(", ") || propertyTypeDescriptionName;

    const selectedUseOfProperty = useOfProperty
        .find((property: any) => property.useproperty_primary_id == formData.use_of_property_id)
        ?.use_of_property || useOfPropertyName;

    const selectedPurposeOfProperty = purposeOfProperty
        .find((property: any) => property.pourpose_primary_id == formData.purpose_of_use_id)
        ?.perpose_of_use || purposeOfPropertyName;

    // Toggle functions
    const toggleUseOfPropertyDropdown = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsDropdownOpenUseOfProperty((prev: any) => !prev);
    };

    const togglePropertyDescriptionDropdown = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsDropdownOpenPropertyDescription((prev: any) => !prev);
    };

    const togglePurposeOfPropertyDropdown = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsDropdownOpenPurposeOfProperty((prev: any) => !prev);
    };

    const handleUseOfPropertySelect = (selectedValue: number) => {
        setFormData((prevState: any) => ({
            ...prevState,
            use_of_property_id: selectedValue,
            // Reset purpose of property
            purpose_of_use_id: "",
        }));
        setIsDropdownOpenUseOfProperty(false);
    };

    const handlePurposeOfPropertySelect = (selectedValue: any) => {
        setFormData((prevState: any) => ({
            ...prevState,
            purpose_of_use_id: selectedValue,
        }));
        setIsDropdownOpenPurposeOfProperty(false);
    };

    if (!isOpen) return null;

    const handleOutsideClick = (e: any) => {
        if (e.target === e.currentTarget) {
            closeModal();
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center" onClick={handleOutsideClick}>
            <div className="bg-white p-6 rounded-lg shadow-lg w-[740px] relative" onClick={(e) => e.stopPropagation()}>
                <h2 className="text-xl font-semibold mb-4">Edit Property Description</h2>

                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">
                        {/* Property Type Dropdown */}
                        <div className="relative z-[4]">
                            <label className="block mb-2 text-sm font-medium text-gray-900">
                                Property Type <span className="text-red-600">*</span>
                            </label>
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={handleDropdownToggle}
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center justify-between"
                                >
                                    {selectedPropertyTypeName || "Select Property Type"}
                                    <i className="ri-arrow-down-s-line ri-lg"></i>
                                </button>

                                {isDropdownOpen && (
                                    <select
                                        value={formData.property_type_id}
                                        onChange={(e) => handlePropertyTypeSelect(Number(e.target.value))}
                                        className="absolute top-full left-0 w-full bg-gray-50 border border-gray-300 text-sm rounded-lg focus:outline-0 block p-2.5 z-10"
                                        size={10}
                                    >
                                        <option value="" disabled>Select Property Type</option>
                                        {propertyTypes.map((property: any) => (
                                            <option key={property.type_primary_id} value={property.type_primary_id}>
                                                {property.type_name}
                                            </option>
                                        ))}
                                        {loading && <option disabled>Loading...</option>}
                                    </select>
                                )}
                            </div>
                        </div>

                        {/* Property Type Description Dropdown */}
                        <div className="relative z-[3]">
                            <label className="block mb-2 text-sm font-medium text-gray-900">
                                Property Type Description <span className="text-red-600">*</span>
                            </label>
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={togglePropertyDescriptionDropdown}
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center justify-between"
                                >
                                    {selectedPropertyDescriptions || "Select Property Type Description"}
                                    <i className="ri-arrow-down-s-line ri-lg"></i>
                                </button>

                                {isDropdownOpenPropertyDescription && (
                                    <div className="absolute top-full left-0 w-full bg-gray-50 border border-gray-300 rounded-lg p-2.5 z-10 max-h-60 overflow-auto">
                                        {propertyDescriptions.map((property: any) => (
                                            <div key={property.id} className="flex items-center space-x-2 py-1">
                                                <input
                                                    type="checkbox"
                                                    id={`prop_desc_${property.id}`}
                                                    value={property.id}
                                                    checked={formData.property_type_description_id?.split(",").includes(String(property.id))}
                                                    onChange={handlePropertyTypeDescriptionSelect}
                                                    className="text-blue-600"
                                                />
                                                <label htmlFor={`prop_desc_${property.id}`} className="text-sm text-gray-900">
                                                    {property.type_description_name}
                                                </label>
                                            </div>
                                        ))}
                                        {loadingPropertyDescription && <p className="text-sm">Loading...</p>}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Use Of Property Dropdown */}
                        <div className="relative z-[1]">
                            <label className="block mb-2 text-sm font-medium text-gray-900">
                                Use Of The Property <span className="text-red-600">*</span>
                            </label>
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={toggleUseOfPropertyDropdown}
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center justify-between"
                                >
                                    {selectedUseOfProperty || "Select Use Of Property"}
                                    <i className="ri-arrow-down-s-line ri-lg"></i>
                                </button>

                                {isDropdownOpenUseOfProperty && (
                                    <select
                                        value={formData.use_of_property_id}
                                        onChange={(e) => handleUseOfPropertySelect(Number(e.target.value))}
                                        className="absolute top-full left-0 w-full bg-gray-50 border border-gray-300 text-sm rounded-lg focus:outline-0 block p-2.5 z-10"
                                        size={10}
                                    >
                                        <option value="" disabled>Select Use Of Property</option>
                                        {useOfProperty.map((property: any) => (
                                            <option key={property.useproperty_primary_id} value={property.useproperty_primary_id}>
                                                {property.use_of_property}
                                            </option>
                                        ))}
                                        {loadingUseOfProperty && <option disabled>Loading...</option>}
                                    </select>
                                )}
                            </div>
                        </div>

                        {/* Purpose Of Property Dropdown */}
                        <div className="relative z-[1]">
                            <label className="block mb-2 text-sm font-medium text-gray-900">
                                Purpose Of Property <span className="text-red-600">*</span>
                            </label>
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={togglePurposeOfPropertyDropdown}
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center justify-between"
                                >
                                    {selectedPurposeOfProperty || "Select Purpose Of Property"}
                                    <i className="ri-arrow-down-s-line ri-lg"></i>
                                </button>

                                {isDropdownOpenPurposeOfProperty && (
                                    <select
                                        value={formData.purpose_of_use_id}
                                        onChange={(e) => handlePurposeOfPropertySelect(e.target.value)}
                                        className="absolute top-full left-0 w-full bg-gray-50 border border-gray-300 text-sm rounded-lg focus:outline-0 block p-2.5 z-10"
                                        size={10}
                                    >
                                        <option value="" disabled>Select Purpose Of Property</option>
                                        {purposeOfProperty.map((property: any) => (
                                            <option key={property.pourpose_primary_id} value={property.pourpose_primary_id}>
                                                {property.perpose_of_use}
                                            </option>
                                        ))}
                                        {loadingPurposeOfProperty && <option disabled>Loading...</option>}
                                    </select>
                                )}
                            </div>
                        </div>

                        {/* Description Name with Tags */}
                        <div className="col-span-2">
                            <label className="block text-sm mb-2 font-medium text-gray-900">
                                Property Description <span className="text-red-600">*</span>
                            </label>
                            <div className="flex items-end gap-4">
                                <div className="w-full flex flex-col gap-y-2">
                                    <div className="flex flex-wrap gap-2 border p-2 rounded-lg min-h-[42px]">
                                        {tags.map((tag) => (
                                            <span
                                                key={tag.id}
                                                className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full flex items-center gap-1 text-sm"
                                            >
                                                {tag.name}
                                                <button
                                                    type="button"
                                                    onClick={() => removeTag(tag.id)}
                                                    className="text-red-500 hover:text-red-700 text-lg leading-none"
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                        <input
                                            type="text"
                                            value={inputValue}
                                            onChange={(e) => setInputValue(e.target.value)}
                                            onKeyDown={handleKeyPress}
                                            className="flex-1 min-w-[150px] outline-none p-1"
                                            placeholder="Type and press Enter to add tags"
                                        />
                                    </div>
                                    {errors.description_name && (
                                        <p className="text-sm text-red-600">{errors.description_name}</p>
                                    )}
                                </div>
                                <button
                                    type="submit"
                                    className="text-white bg-blue-700 hover:bg-blue-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center whitespace-nowrap"
                                >
                                    Update
                                </button>
                            </div>
                        </div>
                    </div>
                </form>

                {successMessage && (
                    <div className="mt-4 p-3 bg-green-600 text-white rounded-lg flex justify-between items-center">
                        <span>{successMessage}</span>
                        <button type="button" onClick={() => setSuccessMessage('')} className="text-white text-xl leading-none">×</button>
                    </div>
                )}

                {dataExistMessage && (
                    <div className="mt-4 p-3 bg-yellow-600 text-white rounded-lg flex justify-between items-center">
                        <span>{dataExistMessage}</span>
                        <button type="button" onClick={() => setDataExistMessage('')} className="text-white text-xl leading-none">×</button>
                    </div>
                )}

                <button
                    type="button"
                    onClick={closeModal}
                    className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl"
                >
                    ×
                </button>
            </div>
        </div>
    );
};

export default PropertyDescriptionEdit;